import React from 'react';
import { useTranslation } from 'react-i18next';

const StatusChip = ({ status }) => {
  const map = {
    dry_run: 'bg-muted text-muted-foreground',
    applied: 'bg-success/10 text-success',
    failed: 'bg-error/10 text-error',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${map[status] || 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
};

/**
 * Read-only table of past data_imports for the current agency.
 * onRowClick(row) opens the detail modal.
 */
const ImportHistoryTable = ({ rows = [], onRowClick }) => {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        {t('tcn.imports.empty', 'No imports yet — drop your first Excel on the Upload tab.')}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
          <tr>
            <th className="text-start px-4 py-2 font-medium">{t('tcn.imports.col.date', 'Date')}</th>
            <th className="text-start px-4 py-2 font-medium">{t('tcn.imports.col.period', 'Period')}</th>
            <th className="text-start px-4 py-2 font-medium">{t('tcn.imports.col.mode', 'Mode')}</th>
            <th className="text-start px-4 py-2 font-medium">{t('tcn.imports.col.lang', 'Lang')}</th>
            <th className="text-end px-4 py-2 font-medium">{t('tcn.imports.col.rows', 'Rows')}</th>
            <th className="text-end px-4 py-2 font-medium">{t('tcn.imports.col.creators', 'Creators')}</th>
            <th className="text-end px-4 py-2 font-medium">{t('tcn.imports.col.managers', 'Managers')}</th>
            <th className="text-end px-4 py-2 font-medium">{t('tcn.imports.col.departed', 'Departed')}</th>
            <th className="text-start px-4 py-2 font-medium">{t('tcn.imports.col.status', 'Status')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => onRowClick?.(r)}
              className="hover:bg-muted/40 cursor-pointer transition-colors"
            >
              <td className="px-4 py-2 tabular-nums text-xs text-muted-foreground whitespace-nowrap">
                {r.created_at
                  ? new Date(r.created_at).toLocaleString(undefined, {
                      year: '2-digit',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
              </td>
              <td className="px-4 py-2 tabular-nums whitespace-nowrap" dir="ltr">
                {r.data_period_start ?? '—'} → {r.data_period_end ?? '—'}
              </td>
              <td className="px-4 py-2"><span className="font-mono text-xs">{r.mode}</span></td>
              <td className="px-4 py-2"><span className="font-mono text-xs">{r.detected_language}</span></td>
              <td className="px-4 py-2 text-end tabular-nums">{r.rows_total ?? 0}</td>
              <td className="px-4 py-2 text-end tabular-nums">
                {r.rows_creators_created ?? 0}+ / {r.rows_creators_upserted ?? 0}↻
              </td>
              <td className="px-4 py-2 text-end tabular-nums">
                {r.rows_managers_created ?? 0}+ / {r.rows_managers_upserted ?? 0}↻
              </td>
              <td className="px-4 py-2 text-end tabular-nums">{r.rows_marked_departed ?? 0}</td>
              <td className="px-4 py-2"><StatusChip status={r.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ImportHistoryTable;
